import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { GraduationCap, ArrowLeft, FileText, Award, AlertCircle, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EmitirDocumentoPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tipoInicial = searchParams.get('tipo') === 'certificado' ? 'certificado' : 'constancia';
  
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  
  const [tipo, setTipo] = useState(tipoInicial);
  const [dni, setDni] = useState('');
  const [email, setEmail] = useState('');

  // Actualizar tipo cuando cambian los searchParams (navegación entre páginas)
  useEffect(() => {
    const nuevoTipo = searchParams.get('tipo') === 'certificado' ? 'certificado' : 'constancia';
    setTipo(nuevoTipo);
  }, [searchParams]);

  useEffect(() => {
    fetchCurso();
  }, [id]);

  const fetchCurso = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/cursos/${id}`);
      setCurso(res.data);
    } catch (err) {
      setError('Curso no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dni.trim() || !email.trim()) {
      setError('Completá todos los campos');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setPdfUrl(null);
    
    try {
      // Use fetch instead of axios to avoid issues with error handling in blob/arraybuffer responses
      const response = await fetch(
        `${API_URL}/capacitaciones/cursos/${id}/emitir/${tipo}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dni, email }),
        }
      );
      
      if (!response.ok) {
        // Handle error responses - try to get JSON error message
        try {
          const errorData = await response.json();
          setError(errorData.detail || 'Error al procesar la solicitud.');
        } catch {
          // Fallback error messages based on status code
          if (response.status === 404) {
            setError('No se encontró una inscripción con esos datos. Verificá tu DNI y email.');
          } else if (response.status === 400) {
            setError('Tu asistencia aún no fue registrada. Contactá al administrador.');
          } else {
            setError('Error al generar el documento. Intentá nuevamente.');
          }
        }
        return;
      }
      
      // Success - create blob URL for download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      
    } catch (err) {
      // Network error or fetch failed
      console.error('Fetch error:', err);
      setError('Error de conexión. Verificá tu conexión a internet.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${tipo}_${curso.nombre.replace(/\s+/g, '_')}.pdf`;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!curso) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <GraduationCap className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">Curso no encontrado</p>
        <button onClick={() => goBack()} className="text-emerald-600 hover:underline">
          ← Volver
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {tipo === 'constancia' ? (
                <FileText className="w-10 h-10 text-blue-600 mx-auto mb-2" />
              ) : (
                <Award className="w-10 h-10 text-amber-600 mx-auto mb-2" />
              )}
              Emitir {tipo === 'constancia' ? 'Constancia de Asistencia' : 'Certificado de Capacitación'}
            </CardTitle>
            <p className="text-center text-gray-600 text-sm">{curso.nombre}</p>
          </CardHeader>
          <CardContent>
            {/* Tipo selector */}
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setTipo('constancia')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  tipo === 'constancia'
                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Constancia
              </button>
              <button
                type="button"
                onClick={() => setTipo('certificado')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  tipo === 'certificado'
                    ? 'bg-amber-100 text-amber-700 border-2 border-amber-500'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                }`}
              >
                <Award className="w-4 h-4 inline mr-2" />
                Certificado
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {pdfUrl ? (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <Download className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">¡Documento generado!</h3>
                <p className="text-gray-600 text-sm">
                  Tu {tipo} está lista para descargar.
                </p>
                <Button
                  onClick={handleDownload}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setPdfUrl(null);
                    setDni('');
                    setEmail('');
                  }}
                  className="w-full"
                >
                  Emitir otro documento
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Ingresá los datos con los que te inscribiste para validar tu asistencia y emitir el documento.
                </p>
                
                <div>
                  <Label htmlFor="dni">DNI</Label>
                  <Input
                    id="dni"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                    placeholder="Ingresá tu DNI"
                    data-testid="input-dni-emitir"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ingresá tu email"
                    data-testid="input-email-emitir"
                  />
                </div>

                <Button
                  type="submit"
                  className={`w-full ${tipo === 'constancia' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                  disabled={submitting}
                  data-testid="btn-emitir-documento"
                >
                  {submitting ? 'Generando...' : `Emitir ${tipo === 'constancia' ? 'Constancia' : 'Certificado'}`}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-4 border-t">
              <p className="text-xs text-gray-500 text-center">
                Los documentos incluyen un código QR para verificar su autenticidad.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
